import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Reservation } from '@mews-commander/data-access';
import { getCurrentEnterpriseReservation } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { lt, useFeatureFlag } from '@mews/framework';
import { Accordion, Checkbox, Stack, Tag } from '@optimus-web/core';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useAreReservationOrderItemsFetching, useInvalidateReservationGroupQuery } from '@mews-commander/core/entities';
import { FeatureFlag } from '@mews-commander/enums';
import { CustomerName } from '../../../../components/commander-ui';
import { refetchReservations, toggleReservationChecked, toggleReservationOpened } from '../../actions';
import { areOrderItemsFetching, isReservationChecked, isReservationOpened } from '../../selectors';
import { useReservationModuleIsDigitalKeyStatus } from '../../utils/useIsDigitalKeyStatus';
import { Offset } from '../ReservationModule/styles';
import ResourceLockedToggle from '../ResourceLockedToggle';
import { UpsellContainer } from '../SmartDetail/UpsellContainer';
import ReservationInfo from './ReservationInfo';
import ReservationNavigationButtons from './ReservationNavigationButtons';
import ReservationSecondaryText from './ReservationSecondaryText';
import { ReservationStateName } from './ReservationStateName';

interface OwnProps {
    reservationId: string;
    scope: string;
    offset: number;
}
interface StateProps {
    isChecked: boolean;
    isOpened: boolean;
    reservation: Reservation | undefined;
}
interface DispatchProps {
    toggleChecked: () => void;
    toggleOpened: () => void;
}
type ReservationListItemProps = OwnProps & StateProps & DispatchProps;

const ReservationListItem = ({
    reservationId,
    scope,
    isChecked,
    isOpened,
    toggleChecked,
    reservation,
    toggleOpened,
    offset,
}: ReservationListItemProps) => {
    const isFetchingRedux = useCommanderSelector((s) => areOrderItemsFetching(s, { scope }));
    const isFetchingQuery = useAreReservationOrderItemsFetching(scope);
    const isReservationModuleReactQueryEnabled = useFeatureFlag(FeatureFlag.reservationModuleReactQueryEnabled);
    const isFetching = isReservationModuleReactQueryEnabled ? isFetchingQuery : isFetchingRedux;
    const isDigitalKeyReady = useReservationModuleIsDigitalKeyStatus(reservationId, scope);

    const dispatch = useCommanderThunkDispatch();
    const invalidateReservationData = useInvalidateReservationGroupQuery(scope);
    const invalidateReservationDataRedux = () =>
        dispatch(refetchReservations({ reservationIds: [reservationId], scope }));
    const refetchGroup = isReservationModuleReactQueryEnabled
        ? invalidateReservationData
        : invalidateReservationDataRedux;

    if (!reservation) return null;

    const {
        customerId,
        assignedResourceId,
        requestedCategoryId,
        serviceOrderState,
        reservationTimeState,
        options,
        serviceId,
        startUtc,
    } = reservation;

    return (
        <Accordion
            data-test-reservation-accordion={reservationId}
            expanded={isOpened}
            onClick={toggleOpened}
            title={customerId && <CustomerName id={customerId} />}
            description={
                <ReservationSecondaryText
                    assignedResourceId={assignedResourceId}
                    requestedCategoryId={requestedCategoryId}
                    serviceId={serviceId}
                />
            }
            startSlot={
                <>
                    <Offset offset={offset}>
                        <Checkbox checked={isChecked} onChange={toggleChecked} />
                    </Offset>
                    <ReservationNavigationButtons reservationId={reservationId} />
                </>
            }
            middleSlot={
                <Stack spacing="200">
                    <ReservationStateName
                        serviceOrderState={serviceOrderState}
                        reservationTimeState={reservationTimeState}
                        options={options}
                    />
                    {isDigitalKeyReady && <Tag variant="lavender">{lt('DigitalKeyReady')}</Tag>}
                </Stack>
            }
            body={
                <Stack vertical spacing="200">
                    <UpsellContainer
                        reservationId={reservationId}
                        reservationTimeState={reservationTimeState}
                        startUtc={startUtc}
                    />
                    <ReservationInfo
                        scope={scope}
                        reservationId={reservationId}
                        refetchGroup={refetchGroup}
                        isFetching={isFetching}
                    />
                </Stack>
            }
            endSlot={<ResourceLockedToggle reservationId={reservationId} onSuccess={refetchGroup} />}
        />
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    createStructuredSelector({
        isChecked: isReservationChecked,
        isOpened: isReservationOpened,
        reservation: (state: $TSFixMe, { reservationId }: $TSFixMe) =>
            getCurrentEnterpriseReservation(state, { id: reservationId }),
    }),
    (dispatch: CommanderThunkDispatch, { reservationId, scope }) => ({
        toggleChecked: () => dispatch(toggleReservationChecked({ reservationId, scope })),
        toggleOpened: () => dispatch(toggleReservationOpened({ reservationId, scope })),
    })
)(ReservationListItem);
